import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.collateralApiEnv;
const CONVERT_XML_API = appConfig.apis.convertXmlApiEnv;
const PERSON_API = appConfig.apis.personApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return res.json();
    throw new ApiError(res.status, `API error: ${res.status}`);
};

const get = async (path: string) => handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const del = async (path: string, data?: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'DELETE', headers: await getHeaders(), body: data ? JSON.stringify(data) : undefined }));

const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const collateralApi = {
    // === Verification Configs (was /guarantees + /collateral-analysis) ===

    getVerificationConfigs: (params?: {
        page?: number; pageSize?: number;
        search?: string; priority?: string;
        guaranteeStatus?: string; intensity?: string;
        operationType?: string; obligationType?: string;
        sortField?: string; sortOrder?: string;
    }) => get(`/verification-configs/page-list${qs(params || {})}`),

    getVerificationConfigView: (uuid: string) => get(`/verification-configs/${uuid}/page-view`),

    getVerificationConfigSection: (creditOperationId: string) => get(`/verification-configs/${creditOperationId}/page-section`),

    getConfigDrawees: (uuid: string) => get(`/verification-configs/${uuid}/drawees`),

    createVerificationConfig: (data: {
        credit_operation_id: string;
        criteria: { criteria_type: string; timing: string }[];
        receivable_ids: string[];
        receivables?: { receivable_id: string; debtor_cpf_cnpj: string; debtor_name?: string; [key: string]: unknown }[];
        analysis_mode?: string;
        sample_count?: number;
        operation_code?: string;
        operation_type?: string;
        obligation_type?: string;
        assignor_name?: string;
        assignor_tax_id?: string;
        total_receivables?: number;
        total_amount?: number;
        trader_name?: string;
    }) => post(`/verification-configs`, data),

    updateVerificationConfig: (uuid: string, data: Record<string, unknown>) =>
        put(`/verification-configs/${uuid}`, data),

    editVerificationCriteria: (uuid: string, criteria: { criteria_type: string; timing: string }[], extra?: { operationType?: string; obligationType?: string; traderName?: string; analysisMode?: string; sampledTitles?: number; debtorNotificationDone?: boolean }) =>
        put(`/verification-configs/${uuid}/criteria`, { criteria, ...extra }),

    registerChecks: (uuid: string, receivableIds: string[], checks: { criteria_type: string; received?: boolean; status?: string; observation?: string }[], receivableStatus?: string, receivableObservation?: string) =>
        put(`/verification-configs/${uuid}/checks`, { receivable_ids: receivableIds, checks, receivable_status: receivableStatus, receivable_observation: receivableObservation }),

    // === Receivables ===

    getReceivables: (params?: {
        page?: number; pageSize?: number;
        search?: string; status?: string;
        allocationStatus?: string;
        sortField?: string; sortOrder?: string;
    }) => get(`/receivables/page-list${qs(params || {})}`),

    // === Drawee Confirmations (was /validation) ===

    getDraweeConfirmations: (params?: {
        page?: number; pageSize?: number;
        status?: string; search?: string;
        sortField?: string; sortOrder?: string;
        priority?: string;
    }) => get(`/drawee-confirmations/page-list${qs(params || {})}`),

    getDraweeConfirmationTotals: () => get(`/drawee-confirmations/totals`),

    getDraweeConfirmationView: (uuid: string) => get(`/drawee-confirmations/${uuid}/page-view`),

    confirmDrawee: (uuid: string, data: { receivable_ids?: string[]; channel: string; user?: string }) =>
        put(`/drawee-confirmations/${uuid}`, data),

    updateDraweeConfirmation: (uuid: string, data: Record<string, unknown>) =>
        put(`/drawee-confirmations/${uuid}`, data),

    revokeDraweeConfirmation: (uuid: string, data?: { user?: string }) =>
        del(`/drawee-confirmations/${uuid}`, data),

    recordInteraction: (uuid: string, action: string, observation?: string) =>
        post(`/drawee-confirmations/${uuid}/interactions`, { action, observation }),

    assignContact: (uuid: string, contactId: string) =>
        put(`/drawee-confirmations/${uuid}/contact`, { contact_id: contactId }),

    rejectReceivable: (uuid: string, receivableId: string, observation: string) =>
        put(`/drawee-confirmations/${uuid}/receivables/${receivableId}/rejection`, { observation }),

    revokeRejection: (uuid: string, receivableId: string) =>
        del(`/drawee-confirmations/${uuid}/receivables/${receivableId}/rejection`),

    // === Convert XML (unchanged) ===

    getOperationData: async (operationCode: string, operationId?: string) => {
        const headers = await getHeaders();
        headers['x-api-key'] = CONVERT_XML_API.key;
        let id = operationId;
        if (!id) {
            const listRes = await fetch(`${CONVERT_XML_API.address}/operations?search=${encodeURIComponent(operationCode)}&pageSize=10`, { headers });
            const listData = await listRes.json();
            const op = listData?.list?.find((o: Record<string, unknown>) => o.operationCode === operationCode);
            if (!op) throw new ApiError(404, 'Operation not found');
            id = op.id;
        }
        const res = await fetch(`${CONVERT_XML_API.address}/operations/${id}`, { headers });
        return handleResponse(res);
    },

    // === Person search (unchanged) ===

    searchPerson: async (query: string): Promise<{ id: string; name: string }[]> => {
        const headers = await getHeaders();
        headers['x-api-key'] = PERSON_API.key;
        const res = await fetch(`${PERSON_API.address}/individual/page-list?name=${encodeURIComponent(query)}&page=0&size=10&sort=id,DESC`, { headers });
        if (!res.ok) return [];
        const data = await res.json();
        return (data.list || []).map((p: any) => ({ id: p.id || p.personId, name: p.name || p.fullName }));
    },

    searchLegalPerson: async (query: string): Promise<any[]> => {
        const headers = await getHeaders();
        headers['x-api-key'] = PERSON_API.key;
        const res = await fetch(`${PERSON_API.address}/search/legal_person/${encodeURIComponent(query)}`, { headers });
        if (!res.ok) return [];
        const data = await res.json();
        return data.results || [];
    },

    getInternalManagers: async (): Promise<string[]> => {
        const headers = await getHeaders();
        headers['x-api-key'] = PERSON_API.key;
        const res = await fetch(`${PERSON_API.address}/legal-persons/internal-managers`, { headers });
        if (!res.ok) return [];
        const data = await res.json();
        return data.list || [];
    },
};
